import { Avatar, Box } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useMemo } from "react";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import ActionMenu from "@/components/app/ActionMenu";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
} from "@/components/app/provider/patient/chart";
import { applyDefaultColumnSettings, DGT_Pagination } from "@/utils/utils";
import {
  MANAGE_PATIENTS_LABEL,
  MANAGE_PATIENTS_TABLE_HEIGHT,
  PATIENT_COLUMN_LABEL,
  PATIENT_DOB_FORMAT,
  PATIENT_ENROLLED_DATE_FORMAT,
  PATIENT_ENROLLMENT_STATUS_LABEL,
  PATIENT_ROW_ACTION_OPTIONS,
} from "../group-visit-constant";
import { GroupPatient, PatientRowAction } from "../group-visit-types";
import { getInitials, getPatientStatusVariant } from "../group-visit-utils";

export type GroupPatientTableProps = {
  patients: GroupPatient[];
  isLoading?: boolean;
  totalCount?: number;
  pagination?: DGT_Pagination;
  onPaginationChange?: (value: DGT_Pagination) => void;
  /** Rows ticked for removal; omit to hide the checkboxes. */
  selection?: GridRowSelectionModel;
  onSelectionChange?: (value: GridRowSelectionModel) => void;
  onRowAction: (action: PatientRowAction, patient: GroupPatient) => void;
  searchKey?: string;
  canEdit?: boolean;
};

const AVATAR_SIZE = 26;

/**
 * Roster grid for a group's enrolled patients.
 *
 * @author Rajkumar Rathod
 */
const GroupPatientTable = ({
  patients,
  isLoading,
  totalCount,
  pagination,
  onPaginationChange,
  selection,
  onSelectionChange,
  onRowAction,
  searchKey,
  canEdit = true,
}: GroupPatientTableProps) => {
  const isSelectable = !!onSelectionChange && canEdit;
  const columns = useMemo<GridColDef<GroupPatient>[]>(
    () =>
      applyDefaultColumnSettings<GroupPatient>([
        {
          field: "name",
          headerName: PATIENT_COLUMN_LABEL.patient,
          flex: 1,
          minWidth: 170,
          renderCell: ({ row }) => (
            <Flex alignItems="center" gap={1}>
              <Avatar
                sx={{
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  fontSize: "10px",
                  bgcolor: CHART_COLORS.bgSecondaryBlue,
                  color: CHART_COLORS.btnSecondaryText,
                }}
              >
                {getInitials(row.name)}
              </Avatar>
              <Text isTabletext>{row.name}</Text>
            </Flex>
          ),
        },
        // {
        //   field: "mrn",
        //   headerName: PATIENT_COLUMN_LABEL.mrn,
        //   flex: 0.8,
        //   minWidth: 130,
        //   renderCell: ({ row }) => <Text isTabletext>{row.mrn ?? "-"}</Text>,
        // },
        {
          field: "birthDate",
          headerName: PATIENT_COLUMN_LABEL.dob,
          flex: 0.8,
          minWidth: 120,
          renderCell: ({ row }) => (
            <Text isTabletext>
              {row.birthDate
                ? dayjs(row.birthDate).format(PATIENT_DOB_FORMAT)
                : "-"}
            </Text>
          ),
        },
        {
          field: "paymentMode",
          headerName: PATIENT_COLUMN_LABEL.paymentMode,
          flex: 0.8,
          minWidth: 130,
          renderCell: ({ row }) => (
            <Text isTabletext>{row.paymentMode ?? "-"}</Text>
          ),
        },
        {
          field: "enrolledAt",
          headerName: PATIENT_COLUMN_LABEL.enrolled,
          flex: 0.8,
          minWidth: 130,
          renderCell: ({ row }) => (
            <Text isTabletext>
              {row.enrolledAt
                ? dayjs(row.enrolledAt).format(PATIENT_ENROLLED_DATE_FORMAT)
                : "-"}
            </Text>
          ),
        },
        {
          field: "status",
          headerName: PATIENT_COLUMN_LABEL.status,
          flex: 0.7,
          minWidth: 120,
          renderCell: ({ row }) => (
            <StatusChip
              label={PATIENT_ENROLLMENT_STATUS_LABEL[row.status]}
              variant={getPatientStatusVariant(row.status)}
            />
          ),
        },
        {
          field: "attendance",
          headerName: PATIENT_COLUMN_LABEL.attendance,
          flex: 0.6,
          minWidth: 120,
          renderCell: ({ row }) => (
            <Text isTabletext>
              {row.totalSessions
                ? `${row.attendedSessions ?? 0} / ${row.totalSessions}`
                : MANAGE_PATIENTS_LABEL.attendanceEmpty}
            </Text>
          ),
        },
        {
          field: "actions",
          headerName: "",
          width: 60,
          renderCell: ({ row }) =>
            canEdit ? (
              <ActionMenu
                options={PATIENT_ROW_ACTION_OPTIONS}
                onActionClick={(action) =>
                  onRowAction(action as PatientRowAction, row)
                }
              />
            ) : null,
        },
      ]),
    [onRowAction, canEdit]
  );

  return (
    // The grid clears its own border, so the card outline lives on this wrapper.
    <Box
      sx={{
        height: MANAGE_PATIENTS_TABLE_HEIGHT,
        border: `1px solid ${CHART_COLORS.border}`,
        borderRadius: CHART_DIMENSIONS.borderRadius,
        overflow: "hidden",
        backgroundColor: CHART_COLORS.surfaceWhite,
      }}
    >
      <CustomDataGridV2
        rows={patients}
        columns={columns}
        getRowId={(row) => row.uuid}
        loading={isLoading}
        rowCount={totalCount}
        paginationModel={pagination}
        onPaginationModelChange={onPaginationChange}
        hideFooter={!pagination}
        checkboxSelection={isSelectable}
        // Select-all must list the row ids; the exclude model reports zero selected rows.
        disableRowSelectionExcludeModel
        rowSelectionModel={selection}
        onRowSelectionModelChange={onSelectionChange}
        searchString={searchKey}
      />
    </Box>
  );
};

export default GroupPatientTable;
